import React, { useCallback, useEffect, useRef, useState } from "react";
import { listMedia, uploadMedia, replaceMedia, deleteMedia, mediaUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { toast } from "sonner";
import { Upload, Trash2, RefreshCw, Search, FileText, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const KINDS = ["all", "logo", "author_photo", "book_cover", "sample_pdf", "trailer_thumbnail", "video", "research_image", "event_image", "other"];

export default function AdminMedia() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploadKind, setUploadKind] = useState("book_cover");
  const [alt, setAlt] = useState("");
  const [caption, setCaption] = useState("");
  const fileRef = useRef();
  const replaceRef = useRef();

  const load = useCallback(
    () => listMedia({ kind: kind === "all" ? undefined : kind, search: q || undefined }).then(setItems),
    [kind, q],
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { load(); }, 300); return () => clearTimeout(t); }, [q, load]);

  const doUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { await uploadMedia(file, { kind: uploadKind, alt_text: alt, caption }); toast.success("Uploaded"); load(); setAlt(""); setCaption(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const doReplace = async (e) => {
    const file = e.target.files?.[0]; if (!file || !selected) return;
    try { await replaceMedia(selected.id, { file }); toast.success("File replaced (URL preserved)"); load(); }
    catch { toast.error("Replace failed"); } finally { e.target.value = ""; }
  };
  const saveMeta = async () => {
    try { await replaceMedia(selected.id, { alt_text: selected.alt_text, caption: selected.caption, kind: selected.kind }); toast.success("Metadata saved"); load(); }
    catch { toast.error("Save failed"); }
  };
  const doDelete = async () => {
    if (!window.confirm("Move this file to trash?")) return;
    try { await deleteMedia(selected.id); toast.success("Moved to trash"); setSelected(null); load(); } catch { toast.error("Failed (owner only)"); }
  };

  const canEdit = user?.role === "owner" || user?.role === "editor";

  return (
    <div data-testid="admin-media-page">
      <PageHeader eyebrow="Assets" title="Media Library" note="Upload logos, covers, sample PDFs, trailer thumbnails, research and event images. Alt text is required for accessibility." />

      {canEdit && (
        <div className="mt-8 border bg-white p-6 grid gap-4 sm:grid-cols-[1fr_1fr_1fr_auto] items-end" style={{ borderColor: "var(--gi-border)" }} data-testid="media-upload-block">
          <div>
            <label className="gi-label">Kind</label>
            <select className="gi-input" value={uploadKind} onChange={e => setUploadKind(e.target.value)} data-testid="media-upload-kind">
              {KINDS.filter(k => k !== "all").map(k => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="gi-label">Alt text</label>
            <input className="gi-input" value={alt} onChange={e => setAlt(e.target.value)} data-testid="media-upload-alt" />
          </div>
          <div>
            <label className="gi-label">Caption</label>
            <input className="gi-input" value={caption} onChange={e => setCaption(e.target.value)} data-testid="media-upload-caption" />
          </div>
          <div>
            <input ref={fileRef} type="file" className="hidden" onChange={doUpload} data-testid="media-upload-file" />
            <button className="btn-maroon" onClick={() => fileRef.current?.click()} data-testid="media-upload-btn"><Upload size={12} /> Upload</button>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {KINDS.map(k => (
            <button key={k} onClick={() => setKind(k)} className="px-3 py-1.5 text-[11px] border" style={{ borderColor: kind === k ? "var(--gi-maroon)" : "var(--gi-border)", background: kind === k ? "var(--gi-maroon)" : "#fff", color: kind === k ? "#fff" : "var(--gi-charcoal-2)", textTransform: "uppercase", letterSpacing: "0.08em" }} data-testid={`media-filter-${k}`}>{k}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 border px-3 bg-white" style={{ borderColor: "var(--gi-border)" }}>
          <Search size={14} style={{ color: "var(--gi-muted)" }} />
          <input placeholder="Search" className="py-2 text-sm outline-none" value={q} onChange={e => setQ(e.target.value)} data-testid="media-search" />
        </div>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
          {items.length === 0 && <div className="col-span-full text-sm p-8 text-center" style={{ color: "var(--gi-muted)" }} data-testid="media-empty">No media yet.</div>}
          {items.map(m => (
            <button key={m.id} onClick={() => setSelected(m)} className="text-left border bg-white p-3 hover:border-[var(--gi-maroon)]" style={{ borderColor: selected?.id === m.id ? "var(--gi-maroon)" : "var(--gi-border)" }} data-testid={`media-tile-${m.id}`}>
              {m.file_type?.startsWith("image/")
                ? <img src={mediaUrl(m.url)} alt={m.alt_text} className="w-full aspect-square object-cover" />
                : <div className="w-full aspect-square bg-[var(--gi-ivory-2)] flex items-center justify-center"><FileText size={28} style={{ color: "var(--gi-muted)" }} /></div>}
              <div className="mt-2 text-xs truncate" style={{ color: "var(--gi-charcoal)" }}>{m.filename}</div>
              <div className="text-[10px]" style={{ color: "var(--gi-muted)" }}>{m.kind} · {(m.size_bytes / 1024).toFixed(1)} KB</div>
            </button>
          ))}
        </div>

        {selected && (
          <aside className="border bg-white p-6 sticky top-6 h-fit" style={{ borderColor: "var(--gi-border)" }} data-testid="media-detail-panel">
            <div className="font-heading text-lg" style={{ color: "var(--gi-maroon)" }}>{selected.filename}</div>
            <div className="text-xs mt-1" style={{ color: "var(--gi-muted)" }}>{selected.file_type} · {(selected.size_bytes / 1024).toFixed(1)} KB · {new Date(selected.uploaded_at).toLocaleDateString()}</div>
            {selected.file_type?.startsWith("image/") && <img src={mediaUrl(selected.url)} alt={selected.alt_text} className="w-full mt-4 border" style={{ borderColor: "var(--gi-border)" }} />}
            <div className="mt-4 space-y-3">
              <div><label className="gi-label">Alt text</label><input className="gi-input" value={selected.alt_text || ""} onChange={e => setSelected({ ...selected, alt_text: e.target.value })} data-testid="media-detail-alt" /></div>
              <div><label className="gi-label">Caption</label><input className="gi-input" value={selected.caption || ""} onChange={e => setSelected({ ...selected, caption: e.target.value })} /></div>
              <div><label className="gi-label">Kind</label>
                <select className="gi-input" value={selected.kind} onChange={e => setSelected({ ...selected, kind: e.target.value })}>{KINDS.filter(k => k !== "all").map(k => <option key={k}>{k}</option>)}</select>
              </div>
              <div><label className="gi-label">URL</label><input className="gi-input" readOnly value={mediaUrl(selected.url)} /></div>
              {canEdit && (
                <>
                  <button className="btn-maroon w-full justify-center" onClick={saveMeta} data-testid="media-save-meta">Save metadata</button>
                  <input ref={replaceRef} type="file" className="hidden" onChange={doReplace} data-testid="media-replace-file" />
                  <button className="btn-outline w-full justify-center" onClick={() => replaceRef.current?.click()} data-testid="media-replace-btn"><RefreshCw size={12} /> Replace file (URL preserved)</button>
                  {user.role === "owner" && <button className="btn-ghost w-full justify-center" onClick={doDelete} data-testid="media-delete-btn"><Trash2 size={12} /> Move to trash</button>}
                </>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
